import { moveItemInArray } from '@angular/cdk/drag-drop';
import { on } from '@ngrx/store';

import { User } from '@ninety/platform/models/_shared/user';
import { SortDirection } from '@ninety/platform/models/enums/sort-direction';
import { Issue } from '@ninety/platform/models/issues/issue';
import { IssueSortField, IssueSortFieldEnum } from '@ninety/platform/models/issues/issues-sort-field';
import { RocksIssuesEvents } from '@ninety/platform/state';

import { issuesEntityAdapter } from './issues.adapter';
import { IssuesState } from './issues.model';

/**
 * Helper function to sort issues by rating
 */
function sortIssuesByRating(issues: Issue[], ascending: boolean): void {
  issues.sort((a, b) => {
    const ratingA = a.rating ?? 0;
    const ratingB = b.rating ?? 0;
    return ascending ? ratingA - ratingB : ratingB - ratingA;
  });
}

/**
 * Helper function to get user name for sorting
 */
function getUserNameForSorting(user: User): string {
  if (!user) return '';
  const firstName = user.metadata?.name?.first?.trim() ?? '';
  const lastName = user.metadata?.name?.last?.trim() ?? '';
  return `${firstName} ${lastName}`.trim().toLowerCase();
}

/**
 * Helper function to sort issues by user name
 */
function sortIssuesByUser(issues: Issue[], ascending: boolean): void {
  issues.sort((a, b) => {
    const userNameA = getUserNameForSorting(a.user);
    const userNameB = getUserNameForSorting(b.user);
    return ascending
      ? userNameA < userNameB
        ? -1
        : userNameB < userNameA
          ? 1
          : 0
      : userNameB < userNameA
        ? -1
        : userNameA < userNameB
          ? 1
          : 0;
  });
}

function isPlanningBoardSortField(
  sortField: IssueSortField
): sortField is IssueSortFieldEnum.rating | IssueSortFieldEnum.user {
  return sortField === IssueSortFieldEnum.rating || sortField === IssueSortFieldEnum.user;
}

/**
 * Reducer handler for sorting issues locally and updating ordinals
 * Used by rocks planning board
 */
export const sortIssuesLocalAndUpdateOrdinalsReducer = on(
  RocksIssuesEvents.sortIssuesLocalAndUpdateOrdinals,
  (state: IssuesState, { sortField, sortDirection }): IssuesState => {
    if (!isPlanningBoardSortField(sortField)) {
      return state;
    }

    const issues = [...Object.values(state.issues.entities)];

    if (sortField === IssueSortFieldEnum.rating) {
      sortIssuesByRating(issues, sortDirection === SortDirection.ASC);
    } else if (sortField === IssueSortFieldEnum.user) {
      sortIssuesByUser(issues, sortDirection === SortDirection.ASC);
    }

    // Normalize planningBoardOrdinal to 0-based to match server contract
    const issuesWithOrdinals = issues.map((issue, index) => ({
      ...issue,
      planningBoardOrdinal: index,
    }));

    return {
      ...state,
      issues: issuesEntityAdapter.setAll(issuesWithOrdinals, state.issues),
    };
  }
);

export const updateOrdinalsOnDragDropReducer = on(
  RocksIssuesEvents.updateOrdinalsOnDragDrop,
  (state: IssuesState, { previousIndex, currentIndex }): IssuesState => {
    // Sort by planningBoardOrdinal so the translated full-array indices align correctly
    const issues = [...Object.values(state.issues.entities)].sort(
      (a, b) => (a.planningBoardOrdinal ?? 0) - (b.planningBoardOrdinal ?? 0)
    );
    moveItemInArray(issues, previousIndex, currentIndex);
    const issuesWithOrdinals = issues.map((issue, index) => ({
      ...issue,
      planningBoardOrdinal: index,
    }));

    return {
      ...state,
      issues: issuesEntityAdapter.setAll(issuesWithOrdinals, state.issues),
    };
  }
);

const sortIssuesLocallyWithoutOrdinals = (
  state: IssuesState,
  sortField: IssueSortField,
  sortDirection: SortDirection
): IssuesState => {
  if (!isPlanningBoardSortField(sortField)) {
    return state;
  }

  const issues = [...Object.values(state.issues.entities)];

  if (sortField === IssueSortFieldEnum.rating) {
    sortIssuesByRating(issues, sortDirection === SortDirection.ASC);
  } else if (sortField === IssueSortFieldEnum.user) {
    sortIssuesByUser(issues, sortDirection === SortDirection.ASC);
  }

  // Reorder entities to match sort — ordinals are intentionally left unchanged
  return {
    ...state,
    issues: issuesEntityAdapter.setAll(issues, state.issues),
  };
};

export const sortIssuesLocalFromBroadcastReducer = on(
  RocksIssuesEvents.sortIssuesLocalFromBroadcast,
  (state: IssuesState, { sortField, sortDirection }): IssuesState =>
    sortIssuesLocallyWithoutOrdinals(state, sortField, sortDirection)
);

export const sortIssuesLocalOnlyReducer = on(
  RocksIssuesEvents.sortIssuesLocalOnly,
  (state: IssuesState, { sortField, sortDirection }): IssuesState =>
    sortIssuesLocallyWithoutOrdinals(state, sortField, sortDirection)
);
